import { TicketVariant } from '~/domain/experiences/index.server';
import type { DayAvailability } from '~/ui/availability-calendar';

export enum TicketErrorCode {
  NoTicketsSelected = 'no_tickets_selected',
  MinNotMet = 'min_not_met',
  MaxExceeded = 'max_exceeded',
  MultipleOfNotMet = 'multiple_of_not_met',
}

const DESIRED_DEFAULT_QUANTITY = 2;

export type ReservationBounds = {
  minReservationSeats: number;
  maxReservationSeats?: number;
  multipleOf?: number;
};

export function defaultTicketSelection(
  variants: TicketVariant[],
  reservationConfig?: ReservationBounds
) {
  const defaultVariant = variants.find((v) => v.isDefault) ?? variants[0];
  if (!defaultVariant) return [];

  const min = reservationConfig?.minReservationSeats ?? 1;
  const max = reservationConfig?.maxReservationSeats ?? Infinity;
  const multipleOf = reservationConfig?.multipleOf ?? 1;

  let quantity = Math.min(Math.max(DESIRED_DEFAULT_QUANTITY, min), max);
  // Round up to nearest multiple within [min, max].
  if (multipleOf > 1 && quantity % multipleOf !== 0) {
    const rounded = Math.ceil(quantity / multipleOf) * multipleOf;
    quantity = rounded <= max ? rounded : quantity;
  }

  return [
    {
      variantId: defaultVariant.id,
      quantity,
    },
  ];
}

export type ReservationViolation =
  | {
      code: TicketErrorCode.MinNotMet;
      minReservationSeats: number;
      currentTotal: number;
    }
  | {
      code: TicketErrorCode.MaxExceeded;
      maxReservationSeats: number;
      currentTotal: number;
    }
  | {
      code: TicketErrorCode.MultipleOfNotMet;
      multipleOf: number;
      currentTotal: number;
    };

/** Minimal shape needed to identify add-on ids — satisfied by both the domain
 *  `TicketVariant` and the UI `TicketVariantOption`, so seat-counting works on
 *  either the server or the client. */
type AddOnBearer = { addOns?: readonly { id: string }[] };

/**
 * Sums non-addon ticket quantities for a basket-style ticket list. Addon
 * quantities don't consume seats so they're excluded from booking-rule
 * totals (matching `@understory-io/availability`'s `addon/` semantics).
 */
export function countSeatTickets(
  tickets: readonly { variantId: string; quantity: number }[],
  variants: readonly AddOnBearer[]
): number {
  const addOnIds = new Set(
    variants.flatMap((v) => v.addOns?.map((a) => a.id) ?? [])
  );
  return tickets.reduce(
    (sum, t) => (addOnIds.has(t.variantId) ? sum : sum + t.quantity),
    0
  );
}

/**
 * Whether the visitor's currently selected day can be advanced from.
 *
 * The calendar's click path only advances on a day it knows is available —
 * clicking a sold-out (or slotless) day just selects it — so the Continue button
 * has to apply the same rule, or pressing it lands the visitor on a time step
 * with nothing to pick.
 *
 * A day whose month hasn't come back yet is *unknown*, not unavailable: gating on
 * it would disable Continue for the length of every month fetch, and permanently
 * if a fetch fails. Such a day passes, and the next step stays the authority on
 * what is bookable.
 *
 * Shared by both flows' date steps so their Continue buttons can't diverge from
 * their calendars.
 */
export function canContinueFromDate({
  date,
  availability,
  readyMonths,
}: {
  /** The locally selected day (`YYYY-MM-DD`), or `null` when none is. */
  date: string | null;
  availability: Record<string, DayAvailability>;
  /** The `YYYY-MM` months whose statuses have been fetched. */
  readyMonths: ReadonlySet<string>;
}): boolean {
  if (!date) return false;
  // Same `YYYY-MM` key the availability fetcher buckets its months by.
  if (!readyMonths.has(date.slice(0, 7))) return true;
  return availability[date] === 'available';
}

/**
 * The ticket filter to send to the availability query for a given selection.
 *
 * A below-minimum party can't be booked yet, so passing it to the availability
 * service makes every slot come back sold-out. In that case omit the filter so
 * availability reflects real capacity — the reservation minimum is enforced on
 * Continue instead. At/above the minimum the selection is passed through (an
 * over-maximum party then legitimately reads as sold-out).
 *
 * Shared by the calendar/booking-dialog loaders (server) and their live
 * filters (client) so the two never diverge.
 */
export function availabilityTicketFilter(
  tickets: readonly { variantId: string; quantity: number }[],
  variants: readonly AddOnBearer[],
  config: ReservationBounds
): { [variantId: string]: number } {
  if (countSeatTickets(tickets, variants) < config.minReservationSeats) {
    return {};
  }
  const filter: { [variantId: string]: number } = {};
  for (const t of tickets) if (t.quantity > 0) filter[t.variantId] = t.quantity;
  return filter;
}

/**
 * Split a basket-style ticket list into the availability request's
 * `BookingContext` maps: seat-consuming ticket variants and add-ons, keyed by
 * bare variant / add-on id as availability-core expects. Zero-quantity lines
 * are dropped.
 */
export function splitTicketsForBookingContext(
  tickets: readonly { variantId: string; quantity: number }[],
  variants: readonly AddOnBearer[]
): {
  ticketVariants: { [variantId: string]: number };
  addonVariants: { [addonId: string]: number };
} {
  const addOnIds = new Set(
    variants.flatMap((v) => v.addOns?.map((a) => a.id) ?? [])
  );
  const ticketVariants: { [id: string]: number } = {};
  const addonVariants: { [id: string]: number } = {};
  for (const t of tickets) {
    if (t.quantity <= 0) continue;
    const target = addOnIds.has(t.variantId) ? addonVariants : ticketVariants;
    target[t.variantId] = t.quantity;
  }
  return { ticketVariants, addonVariants };
}

/**
 * The availability request's `BookingContext` for a ticket-filter map — the
 * `{ variantId: quantity }` shape `availabilityTicketFilter` produces and the
 * calendar's `?ticket=` params carry. Lets the month feed and the loaders that
 * anchor on it build the same context from the same filter, so the two can't
 * disagree about which days are bookable.
 */
export function bookingContextFromTicketFilter(
  filter: { [variantId: string]: number },
  variants: readonly AddOnBearer[]
) {
  return splitTicketsForBookingContext(
    Object.entries(filter).map(([variantId, quantity]) => ({
      variantId,
      quantity,
    })),
    variants
  );
}

/** Whether a `BookingContext` carries anything at all — an empty one must be
 *  omitted from the request rather than sent as a pair of empty maps. */
export function hasBookingSelection(context: {
  ticketVariants: { [variantId: string]: number };
  addonVariants: { [addonId: string]: number };
}): boolean {
  return (
    Object.keys(context.ticketVariants).length > 0 ||
    Object.keys(context.addonVariants).length > 0
  );
}

/**
 * Validate a ticket selection against the experience's reservation rules.
 * Returns `{ ok: true }` when the cart is empty (the empty-cart guard is a
 * separate concern surfaced via `TicketErrorCode.NoTicketsSelected`).
 */
export function validateReservation(
  tickets: readonly { variantId: string; quantity: number }[],
  variants: readonly AddOnBearer[],
  config: ReservationBounds
): { ok: true } | { ok: false; violation: ReservationViolation } {
  const total = countSeatTickets(tickets, variants);
  if (total === 0) return { ok: true };

  const { maxReservationSeats, minReservationSeats, multipleOf } = config;

  if (maxReservationSeats !== undefined && total > maxReservationSeats) {
    return {
      ok: false,
      violation: {
        code: TicketErrorCode.MaxExceeded,
        maxReservationSeats,
        currentTotal: total,
      },
    };
  }

  if (total < minReservationSeats) {
    return {
      ok: false,
      violation: {
        code: TicketErrorCode.MinNotMet,
        minReservationSeats,
        currentTotal: total,
      },
    };
  }

  if (multipleOf !== undefined && multipleOf > 1 && total % multipleOf !== 0) {
    return {
      ok: false,
      violation: {
        code: TicketErrorCode.MultipleOfNotMet,
        multipleOf,
        currentTotal: total,
      },
    };
  }

  return { ok: true };
}
